import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGE_URL } from "../../api-urls";
import icons from "../../icons.config";

const ProductGrid = ({
  itemData,
  loading,
  error,
  gridType,
  handleGridToggle,
  gridIcon,
  handleItemClick,
  handleHeartClick,
  likedItems,
  isAuthenticated,
  isFilterOpen,
  handlers,
}) => {
  const [visibleItemsCount, setVisibleItemsCount] = useState(10); // Start with 10 items
  const itemsPerPage = 10;
  const loadMoreRef = useRef(null); // Ref for the Load More button
  const [showLoadMore, setShowLoadMore] = useState(true); // Control button visibility
  const lastScrollY = useRef(window.scrollY); // Track last scroll position

  // Calculate total items and visible items
  const totalItems = itemData.length;
  const currentItems = itemData.slice(0, visibleItemsCount);

  // Handle Load More button click
  const handleLoadMore = () => {
    setVisibleItemsCount((prev) => prev + itemsPerPage);
  };

  // Scroll direction detection
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingUp = currentScrollY < lastScrollY.current;

      // Show/hide Load More button based on scroll direction and remaining items
      if (scrollingUp && visibleItemsCount < totalItems) {
        setShowLoadMore(false);
      } else if (!scrollingUp && visibleItemsCount < totalItems) {
        setShowLoadMore(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleItemsCount, totalItems]);

  // Update button visibility when visibleItemsCount or totalItems change
  useEffect(() => {
    if (visibleItemsCount >= totalItems) {
      setShowLoadMore(false); // Hide button when all items are loaded
    } else {
      setShowLoadMore(true); // Show button if more items are available
    }
  }, [visibleItemsCount, totalItems]);

  return (
    <div
      className={`flex-grow transition-all duration-300 ${
        isFilterOpen
          ? "w-full lg:w-[75%] lg:relative lg:-top-0 lg:px-28 lg:ml-auto"
          : "w-full"
      } bg-tertiary relative max-w-7xl h-full`}
      {...handlers}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <p className="text-lg font-medium text-hcolor py-20">Loading...</p>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-full">
          <p className="text-lg font-medium text-hcolor py-20">{error}</p>
        </div>
      ) : itemData.length > 0 ? (
        <>
          <div
            className={`grid ${
              gridType === "single" ? "grid-cols-1 gap-3" : "grid-cols-2"
            } lg:grid-cols-3 gap-6 md:gap-10 lg:gap-6 px-11 md:px-11 lg:px-20 xl:px-14 2xl:px-3`}
          >
            {currentItems.map((item) => (
              <div
                key={item.id}
                className={`text-left flex justify-center relative z-10 ${
                  gridType === "single" ? "mb-12" : "mb-12 lg:mb-0"
                }`}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={item.default_variant.id}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.5 }}
                    className={`cursor-pointer w-full ${
                      gridType === "single"
                        ? "h-96 md:h-[32rem] xl:h-[48rem]"
                        : "h-60 md:h-96 xl:h-screen"
                    } relative xl:pb-36 2xl:pb-32 justify-center items-center aspect-[3/4] overflow-visible`}
                  >
                    <motion.div
                      className="h-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      onClick={() => handleItemClick(item)}
                    >
                      {item.default_variant?.images?.length > 0 ? (
                        <motion.img
                          src={IMAGE_URL + item.default_variant.images[0]}
                          alt={item.product_name || "Product Thumbnail"}
                          className="w-full h-full object-cover transition-all duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 text-gray-500 flex justify-center items-center">
                          No Image Available
                        </div>
                      )}
                    </motion.div>

                    <div
                      className="absolute top-4 right-4 text-primary1 font-light cursor-pointer"
                      onClick={() => handleHeartClick(item)}
                    >
                      <img
                        src={
                          isAuthenticated &&
                          likedItems.some(
                            (likedItem) =>
                              likedItem?.variant_id?.toString() ===
                              item?.default_variant?.id?.toString()
                          )
                            ? icons.filledHeart
                            : icons.heart
                        }
                        alt="Heart Icon"
                        className="w-5 h-5 md:w-7 md:h-7"
                      />
                    </div>

                    {item.product_name && (
                      <motion.h2
                        className="font-light mt-2 font-outfit text-md lg:text-lg text-primary1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.5 }}
                      >
                        {item.product_name}
                      </motion.h2>
                    )}

                    <motion.p
                      className="text-sm mt-1 text-hcolor font-poppins font-light"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.5 }}
                    >
                      {item.default_variant.discount_price
                        ? `Rs. ${item.default_variant.discount_price} INR`
                        : "Price Not Available"}
                    </motion.p>
                  </motion.div>
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {totalItems > 10 && visibleItemsCount < totalItems && (
            <div
              className="flex justify-center items-center mt-6 pb-6"
              ref={loadMoreRef}
            >
              <motion.button
                onClick={handleLoadMore}
                className={`px-6 py-2 text-hcolor font-outfit font-medium rounded-md border border-hcolor hover:bg-hcolor hover:text-tertiary transition-colors duration-300 ${
                  showLoadMore ? "block" : "hidden"
                }`}
                initial={{ opacity: 0 }}
                animate={{ opacity: showLoadMore ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              >
                Load More
              </motion.button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col justify-center items-center h-full py-20">
          <img
            src={icons.sizeAdded}
            alt="Dress Icon"
            className="w-16 h-16 mb-4"
          />
          <p className="text-lg font-medium text-hcolor">
            No products are available with your desired filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;



// import React, { useState } from "react";

// const PaginationTest = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 10;

//   // Simulate item data (e.g., 50 items for 5 pages, adjust as needed)
//   const totalItems = 50; // Change this to test different scenarios (e.g., 5, 20, 50)
//   const totalPages = Math.ceil(totalItems / itemsPerPage);

//   // Handle page change
//   const handlePageChange = (page) => {
//     if (page >= 1 && page <= totalPages) {
//       setCurrentPage(page);
//     }
//   };

//   // Calculate visible page numbers (show 3 at a time)
//   const getVisiblePages = () => {
//     const maxVisiblePages = 3;
//     let startPage = Math.max(1, currentPage - 1); // Start with current page - 1, but not less than 1
//     let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1); // Show up to 3 pages

//     // Adjust startPage if endPage reaches totalPages
//     if (endPage - startPage + 1 < maxVisiblePages) {
//       startPage = Math.max(1, endPage - maxVisiblePages + 1);
//     }

//     return Array.from(
//       { length: endPage - startPage + 1 },
//       (_, i) => startPage + i
//     );
//   };

//   const visiblePages = getVisiblePages();

//   return (
//     <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
//       <h1 className="text-2xl font-bold mb-4">
//         Pagination Test (Total Items: {totalItems}, Pages: {totalPages})
//       </h1>
//       <p className="mb-4">Current Page: {currentPage}</p>

//       {/* Pagination Controls (only if more than one page) */}
//       {totalPages > 1 && (
//         <div className="flex justify-center items-center mt-6 pb-6">
//           <button
//             onClick={() => handlePageChange(currentPage - 1)}
//             disabled={currentPage === 1}
//             className="px-4 py-2 mx-1 text-[#650D1B] font-sans font-medium rounded-md border border-[#650D1B] disabled:opacity-50 hover:bg-[#650D1B] hover:text-white transition-colors duration-300"
//           >
//             Previous
//           </button>

//           <div className="flex gap-2 mx-4">
//             {visiblePages.map((page) => (
//               <button
//                 key={page}
//                 onClick={() => handlePageChange(page)}
//                 className={`px-3 py-1 rounded-md font-sans ${
//                   currentPage === page
//                     ? "bg-[#650D1B] text-white"
//                     : "text-[#650D1B] border border-[#650D1B] hover:bg-[#650D1B] hover:text-white"
//                 } transition-colors duration-300`}
//               >
//                 {page}
//               </button>
//             ))}
//           </div>

//           <button
//             onClick={() => handlePageChange(currentPage + 1)}
//             disabled={currentPage === totalPages}
//             className="px-4 py-2 mx-1 text-[#650D1B] font-sans font-medium rounded-md border border-[#650D1B] disabled:opacity-50 hover:bg-[#650D1B] hover:text-white transition-colors duration-300"
//           >
//             Next
//           </button>
//         </div>
//       )}

//       {/* Optional: Display simulated items for context */}
//       <div className="mt-4 text-center">
//         <p>
//           Showing items {(currentPage - 1) * itemsPerPage + 1} to{" "}
//           {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
//         </p>
//       </div>
//     </div>
//   );
// };

// export default PaginationTest;




// import React, { useState } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { IMAGE_URL } from "../../api-urls";
// import icons from "../../icons.config";

// const ProductGrid = ({
//   itemData,
//   loading,
//   error,
//   gridType,
//   handleGridToggle,
//   gridIcon,
//   handleItemClick,
//   handleHeartClick,
//   likedItems,
//   isAuthenticated,
//   isFilterOpen,
//   handlers,
// }) => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 10;

//   // Calculate total pages
//   const totalPages = Math.ceil(itemData.length / itemsPerPage);

//   // Calculate items to display on the current page
//   const startIndex = (currentPage - 1) * itemsPerPage;
//   const endIndex = startIndex + itemsPerPage;
//   const currentItems = itemData.slice(startIndex, endIndex);

//   // Handle page change
//   const handlePageChange = (page) => {
//     if (page >= 1 && page <= totalPages) {
//       setCurrentPage(page);
//     }
//   };

//   return (
//     <div
//       className={`flex-grow transition-all duration-300 ${
//         isFilterOpen
//           ? "w-full lg:w-[75%] lg:relative lg:-top-0 lg:px-28 lg:ml-auto"
//           : "w-full"
//       } bg-tertiary relative max-w-7xl h-full`}
//       {...handlers}
//     >
//       {loading ? (
//         <div className="flex justify-center items-center h-full">
//           <p className="text-lg font-medium text-hcolor py-20">Loading...</p>
//         </div>
//       ) : error ? (
//         <div className="flex justify-center items-center h-full">
//           <p className="text-lg font-medium text-hcolor py-20">{error}</p>
//         </div>
//       ) : itemData.length > 0 ? (
//         <>
//           <div
//             className={`grid ${
//               gridType === "single" ? "grid-cols-1 gap-3" : "grid-cols-2"
//             } lg:grid-cols-3 gap-6 md:gap-10 lg:gap-6 px-11 md:px-11 lg:px-20 xl:px-14 2xl:px-3`}
//           >
//             {currentItems.map((item) => (
//               <div
//                 key={item.id}
//                 className={`text-left flex justify-center relative z-10 ${
//                   gridType === "single" ? "mb-1" : "mb-12"
//                 }`}
//               >
//                 <AnimatePresence mode="wait">
//                   <motion.div
//                     key={item.default_variant.id}
//                     initial="initial"
//                     animate="animate"
//                     exit="exit"
//                     transition={{ duration: 0.5 }}
//                     className={`cursor-pointer w-full h-60 md:h-96 md:w-[98%] xl:h-screen relative xl:pb-36 2xl:pb-32 justify-center items-center aspect-[3/4] overflow-visible`}
//                   >
//                     <motion.div
//                       className="h-full"
//                       initial={{ opacity: 0 }}
//                       animate={{ opacity: 1 }}
//                       exit={{ opacity: 0 }}
//                       transition={{ duration: 0.5 }}
//                       onClick={() => handleItemClick(item)}
//                     >
//                       {item.default_variant?.images?.length > 0 ? (
//                         <motion.img
//                           src={IMAGE_URL + item.default_variant.images[0]}
//                           alt={item.product_name || "Product Thumbnail"}
//                           className="w-full h-full object-cover transition-all duration-500"
//                         />
//                       ) : (
//                         <div className="w-full h-full bg-gray-200 text-gray-500 flex justify-center items-center">
//                           No Image Available
//                         </div>
//                       )}
//                     </motion.div>

//                     <div
//                       className="absolute top-4 right-4 text-primary1 font-light cursor-pointer"
//                       onClick={() => handleHeartClick(item)}
//                     >
//                       <img
//                         src={
//                           isAuthenticated &&
//                           likedItems.some(
//                             (likedItem) =>
//                               likedItem?.variant_id?.toString() ===
//                               item?.default_variant?.id?.toString()
//                           )
//                             ? icons.filledHeart
//                             : icons.heart
//                         }
//                         alt="Heart Icon"
//                         className="w-5 h-5 md:w-7 md:h-7"
//                       />
//                     </div>

//                     {item.product_name && (
//                       <motion.h2
//                         className="font-light mt-2 font-outfit text-md lg:text-lg text-primary1"
//                         initial={{ opacity: 0 }}
//                         animate={{ opacity: 1 }}
//                         exit={{ opacity: 0 }}
//                         transition={{ duration: 1.5 }}
//                       >
//                         {item.product_name}
//                       </motion.h2>
//                     )}

//                     <motion.p
//                       className="text-sm mt-1 text-hcolor font-poppins font-light"
//                       initial={{ opacity: 0 }}
//                       animate={{ opacity: 1 }}
//                       exit={{ opacity: 0 }}
//                       transition={{ duration: 1.5 }}
//                     >
//                       {item.default_variant.discount_price
//                         ? `Rs. ${item.default_variant.discount_price} INR`
//                         : "Price Not Available"}
//                     </motion.p>
//                   </motion.div>
//                 </AnimatePresence>
//               </div>
//             ))}
//           </div>

//           {/* Pagination Controls */}
//           {totalPages > 1 && (
//             <div className="flex justify-center items-center mt-6 pb-6">
//               <button
//                 onClick={() => handlePageChange(currentPage - 1)}
//                 disabled={currentPage === 1}
//                 className="px-4 py-2 mx-1 text-hcolor font-outfit font-medium rounded-md border border-hcolor disabled:opacity-50 hover:bg-hcolor hover:text-white transition-colors duration-300"
//               >
//                 Previous
//               </button>

//               {/* Page Numbers */}
//               <div className="flex gap-2 mx-4">
//                 {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
//                   <button
//                     key={page}
//                     onClick={() => handlePageChange(page)}
//                     className={`px-3 py-1 rounded-md font-outfit ${
//                       currentPage === page
//                         ? "bg-hcolor text-white"
//                         : "text-hcolor border border-hcolor hover:bg-hcolor hover:text-white"
//                     } transition-colors duration-300`}
//                   >
//                     {page}
//                   </button>
//                 ))}
//               </div>

//               <button
//                 onClick={() => handlePageChange(currentPage + 1)}
//                 disabled={currentPage === totalPages}
//                 className="px-4 py-2 mx-1 text-hcolor font-outfit font-medium rounded-md border border-hcolor disabled:opacity-50 hover:bg-hcolor hover:text-white transition-colors duration-300"
//               >
//                 Next
//               </button>
//             </div>
//           )}
//         </>
//       ) : (
//         <div className="flex justify-center items-center h-full">
//           <p className="text-lg font-medium text-hcolor py-20">
//             No data available for your desired filters.
//           </p>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ProductGrid;







// import React from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { IMAGE_URL } from "../../api-urls";
// import icons from "../../icons.config";

// const ProductGrid = ({
//   itemData,
//   loading,
//   error,
//   gridType,
//   handleGridToggle,
//   gridIcon,
//   handleItemClick,
//   handleHeartClick,
//   likedItems,
//   isAuthenticated,
//   isFilterOpen,
//   handlers,
// }) => (
//   <div
//     className={`flex-grow transition-all duration-300 ${
//       isFilterOpen
//         ? "w-full lg:w-[75%] lg:relative lg:-top-0 lg:px-28 lg:ml-auto"
//         : "w-full"
//     } bg-tertiary relative max-w-7xl h-full`}
//     {...handlers}
//   >
//     {loading ? (
//       <div className="flex justify-center items-center h-full">
//         <p className="text-lg font-medium text-hcolor py-20">Loading...</p>
//       </div>
//     ) : error ? (
//       <div className="flex justify-center items-center h-full">
//         <p className="text-lg font-medium text-hcolor py-20">{error}</p>
//       </div>
//     ) : itemData.length > 0 ? (
//       <div
//         className={`grid ${
//           gridType === "single" ? "grid-cols-1 gap-3" : "grid-cols-2"
//         } lg:grid-cols-3 gap-6 md:gap-10 lg:gap-6 px-11 md:px-11 lg:px-20 xl:px-14 2xl:px-3`}
//       >
//         {itemData.map((item) => (
//           <div
//             key={item.id}
//             className={`text-left flex justify-center relative z-10 ${
//               gridType === "single" ? "mb-1" : "mb-12"
//             }`}
//           >
//             <AnimatePresence mode="wait">
//               <motion.div
//                 key={item.default_variant.id}
//                 initial="initial"
//                 animate="animate"
//                 exit="exit"
//                 transition={{ duration: 0.5 }}
//                 className={`cursor-pointer w-full h-60 md:h-96 md:w-[98%] xl:h-screen relative xl:pb-36 2xl:pb-32 justify-center items-center aspect-[3/4] overflow-visible`}
//               >
//                 <motion.div
//                   className="h-full"
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   exit={{ opacity: 0 }}
//                   transition={{ duration: 0.5 }}
//                   onClick={() => handleItemClick(item)}
//                 >
//                   {item.default_variant?.images?.length > 0 ? (
//                     <motion.img
//                       src={IMAGE_URL + item.default_variant.images[0]}
//                       alt={item.product_name || "Product Thumbnail"}
//                       className="w-full h-full object-cover transition-all duration-500"
//                     />
//                   ) : (
//                     <div className="w-full h-full bg-gray-200 text-gray-500 flex justify-center items-center">
//                       No Image Available
//                     </div>
//                   )}
//                 </motion.div>

//                 <div
//                   className="absolute top-4 right-4 text-primary1 font-light cursor-pointer"
//                   onClick={() => handleHeartClick(item)}
//                 >
//                   <img
//                     src={
//                       isAuthenticated &&
//                       likedItems.some(
//                         (likedItem) =>
//                           likedItem?.variant_id?.toString() ===
//                           item?.default_variant?.id?.toString()
//                       )
//                         ? icons.filledHeart
//                         : icons.heart
//                     }
//                     alt="Heart Icon"
//                     className="w-5 h-5 md:w-7 md:h-7"
//                   />
//                 </div>

//                 {item.product_name && (
//                   <motion.h2
//                     className="font-light mt-2 font-outfit text-md lg:text-lg text-primary1"
//                     initial={{ opacity: 0 }}
//                     animate={{ opacity: 1 }}
//                     exit={{ opacity: 0 }}
//                     transition={{ duration: 1.5 }}
//                   >
//                     {item.product_name}
//                   </motion.h2>
//                 )}

//                 <motion.p
//                   className="text-sm mt-1 text-hcolor font-poppins font-light"
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   exit={{ opacity: 0 }}
//                   transition={{ duration: 1.5 }}
//                 >
//                   {item.default_variant.discount_price
//                     ? `Rs. ${item.default_variant.discount_price} INR`
//                     : "Price Not Available"}
//                 </motion.p>
//               </motion.div>
//             </AnimatePresence>
//           </div>
//         ))}
//       </div>
//     ) : (
//       <div className="flex justify-center items-center h-full">
//         <p className="text-lg font-medium text-hcolor py-20">
//           No data available for your desired filters.
//         </p>
//       </div>
//     )}
//   </div>
// );

// export default ProductGrid;